import { formatTimeAgo } from '@parlay-picker/shared/formatting';
import type { Notification } from '@parlay-picker/shared/notification';
import type { NotificationDisplayData } from '@parlay-picker/shared/notificationDisplay';
import { Check, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { COLOR_SCHEME_CLASSES } from './colorScheme';
import { getTypeIcon } from './getTypeIcon';

/** Header row: icon circle + type label + timestamp + unread dot (or inline action buttons) */
export function CardHeader({
  notification,
  displayData,
  compact,
  onMarkAsRead,
  onDismiss,
}: {
  notification: Notification;
  displayData: NotificationDisplayData;
  compact?: boolean;
  onMarkAsRead?: (ids: string[]) => Promise<void>;
  onDismiss?: (id: string) => Promise<void>;
}): ReactElement {
  const colors = COLOR_SCHEME_CLASSES[displayData.colorScheme];
  const isUnread = !notification.readAt;

  return (
    <div className="flex items-center gap-2">
      <div
        className={`${compact ? 'w-6 h-6' : 'w-7 h-7'} rounded-full flex items-center justify-center shrink-0 ${colors.iconBg}`}
      >
        {getTypeIcon(
          notification.type,
          `${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} ${colors.iconText}`,
        )}
      </div>
      <span className={`text-xs font-bold uppercase tracking-wide ${colors.badgeText}`}>
        {displayData.label}
      </span>
      <span className="text-xs text-theme-text-tertiary">
        {formatTimeAgo(notification.createdAt)}
      </span>
      {onMarkAsRead && onDismiss ? (
        <div className="flex items-center gap-0.5 ml-auto">
          {isUnread && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead([notification.id]);
              }}
              className="p-1 rounded hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text transition-colors"
              title="Mark as read"
            >
              <Check className="w-3 h-3" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(notification.id);
            }}
            className="p-1 rounded hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-danger transition-colors"
            title="Dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        isUnread && (
          <div
            className={`w-2 h-2 rounded-full ml-auto shrink-0 ${
              displayData.colorScheme === 'success'
                ? 'bg-theme-success'
                : displayData.colorScheme === 'danger'
                  ? 'bg-theme-danger'
                  : displayData.colorScheme === 'primary'
                    ? 'bg-theme-primary'
                    : 'bg-yellow-400'
            }`}
          />
        )
      )}
    </div>
  );
}
